import { logError } from '@/lib/sentry.lib';
import {
    selectBuCode,
    selectOrderNoSource,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { getStorePreferences } from '@repo/api-adapters/store-preferences';
import { API } from '@repo/hooks';
import { queryClient } from '@repo/lib/react-query';
import { type AssetDataType, type AssetType, assetTypes } from '@repo/types/asset';
import { isDraft } from '@repo/types/draft';
import { isISOMOrder } from '@repo/types/order';
import { isQuotation } from '@repo/types/quotation';
import type { CustomerDetails, StorePreferencesConfig } from '@repo/utils';
import { getCountryLanguage } from '@repo/utils';
import type { ShareOption } from '@/components/share/share-options';
import {
    type AssetShareType,
    type AssetShareTypeArray,
    type SendMessagePayload,
    type ShareEventType,
    assetShareOption,
    orderShareOption,
    sendConfirmation,
    sendMessage,
} from '@/api/asset.api';
import { getServiceEndpoint } from '@/api/helpers.api';
import { canPay, canPayOnline, hasNoStockDelivery, hasReservation, isCashAndCarryOrder } from '@/utils/asset.utils';
import { getAppConfig } from './window.utils';

export const shareAsset = async ({
    assetType,
    customerDetails,
    data,
    email,
    locale,
    orderId,
    printer,
    sms,
    termsAndConditions,
    eventType,
}: {
    assetType: AssetType;
    customerDetails: CustomerDetails;
    data: AssetDataType;
    email: ShareOption;
    locale: string;
    orderId: string;
    printer: ShareOption;
    sms: ShareOption;
    termsAndConditions: boolean;
    eventType: ShareEventType;
}) => {
    const storePreferencesConfig = await fetchStorePreferencesConfig(data);

    switch (assetType) {
        case assetTypes.order: {
            return Promise.all(
                shareOrderHandler({
                    storePreferencesConfig,
                    customerDetails,
                    data,
                    email,
                    locale,
                    orderId,
                    printer,
                    sms,
                    termsAndConditions,
                })
            );
        }
        case assetTypes.draft: {
            return Promise.all(
                shareAssetHandler({
                    storePreferencesConfig,
                    orderId,
                    email,
                    printer,
                    sms,
                    locale,
                    eventType,
                    assetType,
                    data,
                    customerDetails,
                })
            );
        }
        case assetTypes.quote: {
            return Promise.all(
                shareAssetHandler({
                    storePreferencesConfig,
                    orderId,
                    email,
                    printer,
                    sms,
                    locale,
                    eventType,
                    assetType,
                    data,
                    customerDetails,
                })
            );
        }
        default: {
            return [];
        }
    }
};

const fetchStorePreferencesConfig = async (data: AssetDataType): Promise<StorePreferencesConfig> => {
    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const { country } = getCountryLanguage();
    const storePreferencesBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storePreferences');

    try {
        return await queryClient.ensureQueryData({
            queryKey: [API.storePreferences, assetBuCode],
            queryFn: () => getStorePreferences(storePreferencesBaseUrl, country, assetBuCode),
        });
    } catch (error) {
        logError(error);
        return { preferences: null };
    }
};

const shareOrderHandler = ({
    customerDetails,
    data,
    email,
    locale,
    orderId,
    printer,
    sms,
    termsAndConditions,
    storePreferencesConfig,
}: {
    customerDetails: CustomerDetails;
    data: AssetDataType;
    email: ShareOption;
    locale: string;
    orderId: string;
    printer: ShareOption;
    sms: ShareOption;
    termsAndConditions: boolean;
    storePreferencesConfig: StorePreferencesConfig;
}) => {
    const promises = [];
    const { isRegisteredCustomer, isRimsCustomer } = customerDetails;
    const orderDetails = generateOrderDetails({
        data,
        isRegisteredCustomer,
        locale,
        orderId,
        storePreferencesConfig,
    });

    if (printer.checked && printer.value) {
        promises.push(
            sendConfirmation(orderShareOption.PRINT, {
                ...orderDetails,
                hasTermsAndConditions: printer.printTermsAndConditions ?? false,
                printerName: printer.value,
                noOfCopies: printer.noOfCopies ?? 1,
            })
        );
    }
    if (sms.checked && sms.value) {
        promises.push(
            sendConfirmation(orderShareOption.SMS, {
                ...orderDetails,
                includePaymentLink: isRimsCustomer ? false : orderDetails.includePaymentLink,
                hasTermsAndConditions: termsAndConditions,
                customerContactValue: sms.value,
            })
        );
    }
    if (email.checked && email.value) {
        promises.push(
            sendConfirmation(orderShareOption.EMAIL, {
                ...orderDetails,
                hasTermsAndConditions: termsAndConditions,
                customerContactValue: email.value,
            })
        );
    }

    return promises;
};

const generateOrderDetails = ({
    data,
    isRegisteredCustomer,
    locale,
    orderId,
    storePreferencesConfig,
}: {
    data: AssetDataType;
    isRegisteredCustomer: boolean;
    locale: string;
    orderId: string;
    storePreferencesConfig: StorePreferencesConfig;
}) => {
    const isIsomOrder = isISOMOrder(data);
    const isQuotationOrder = isQuotation(data);
    const isDraftOrder = isDraft(data);
    const isCustomerEligible = isCustomerEligibleForPaymentLink(isRegisteredCustomer);
    let includePaymentLink = false;
    let cashAndCarryOrder = false;

    if (isIsomOrder) {
        const canBePaid = canPay(data);
        const canBePaidOnline = canPayOnline(data, storePreferencesConfig?.preferences);
        const hasNoStock = hasNoStockDelivery(data);
        cashAndCarryOrder = isCashAndCarryOrder(data);

        includePaymentLink =
            isCustomerEligible && !isDraftOrder && !isQuotationOrder && canBePaid && canBePaidOnline && !hasNoStock;
    }

    const { country } = getCountryLanguage();

    const orderType = selectOrderType(data);
    const orderNoSourceResult = selectOrderNoSource(data);
    const orderNoSource =
        typeof orderNoSourceResult === 'function' ? orderNoSourceResult(country) : (orderNoSourceResult ?? '');

    return {
        orderNo: orderId,
        orderNoSource,
        orderType,
        buCode: selectBuCode(data) ?? '',
        isDraftOrder,
        isQuotation: isQuotationOrder,
        locale,
        includePaymentLink,
        cashAndCarryOrder,
        hasReservation: !isDraftOrder && !isQuotationOrder && hasReservation(data),
    };
};

const isCustomerEligibleForPaymentLink = (isRegisteredCustomer: boolean) => {
    const config = getAppConfig();

    if (config.feature.paymentLinkForRegisteredCustomersOnly?.enable) {
        return isRegisteredCustomer;
    }

    return true;
};

const createSendMessagePayload = ({
    orderId,
    locale,
    eventType,
    entityType,
}: {
    orderId: string;
    locale: string;
    eventType: ShareEventType;
    entityType: 'DRAFT' | 'QUOTATION';
}): SendMessagePayload => ({
    sharingType: [],
    entityType: entityType,
    eventType,
    entityId: orderId,
    locale,
});

const AssetToEntityType = {
    [assetTypes.draft]: 'DRAFT',
    [assetTypes.quote]: 'QUOTATION',
} as const;

const shareAssetHandler = ({
    orderId,
    email,
    printer,
    sms,
    locale,
    eventType,
    assetType,
    data,
    customerDetails,
    storePreferencesConfig,
}: {
    orderId: string;
    email: ShareOption;
    printer: ShareOption;
    sms: ShareOption;
    locale: string;
    eventType: ShareEventType;
    assetType: AssetType;
    data: AssetDataType;
    customerDetails: CustomerDetails;
    storePreferencesConfig: StorePreferencesConfig;
}): Array<Promise<Response>> => {
    if (assetType === assetTypes.order) {
        return [];
    }
    const promises = [];
    const basePayload: SendMessagePayload = createSendMessagePayload({
        orderId,
        locale,
        eventType,
        entityType: AssetToEntityType[assetType],
    });

    const config = getAppConfig();

    const emailData = handleEmailSharing(email);
    const printerData = handlePrinterSharing(printer);
    const smsData = handleSmsSharing(sms);

    const sendSmsViaCustomerMessaging = config.feature.enableCustomerMessagingSmsSharing;
    if (sms.checked && sms.value && assetType === assetTypes.draft && !sendSmsViaCustomerMessaging) {
        const { isRegisteredCustomer } = customerDetails;
        const orderDetails = generateOrderDetails({
            data,
            isRegisteredCustomer,
            locale,
            orderId,
            storePreferencesConfig,
        });
        promises.push(
            sendConfirmation(orderShareOption.SMS, {
                ...orderDetails,
                orderNo: orderId,
                includePaymentLink: false,
                hasTermsAndConditions: false,
                customerContactValue: sms.value,
            })
        );
    }

    const sharingType: AssetShareTypeArray = [
        emailData.emailSharingType,
        printerData.printerSharingType,
        ...(sendSmsViaCustomerMessaging ? [smsData.smsSharingType] : []),
    ].filter((value): value is AssetShareType => Boolean(value));

    if (sharingType.length > 0) {
        promises.push(
            sendMessage({
                ...basePayload,
                ...emailData.emailPayload,
                ...printerData.printerPayload,
                ...smsData.smsPayload,
                sharingType,
            })
        );
    }
    return promises;
};

const handleEmailSharing = (email: ShareOption) => {
    if (email.checked && email.value) {
        return {
            emailPayload: { emailAddress: email.value },
            emailSharingType: assetShareOption.EMAIL,
        };
    }
    return { emailPayload: {}, emailSharingType: undefined };
};

const handlePrinterSharing = (printer: ShareOption) => {
    if (printer.checked && printer.value) {
        const [printerServer, printerName] = printer.value.split('|');
        return {
            printerPayload: {
                printer: printerName,
                printerServer: printerServer,
                noOfCopies: printer.noOfCopies ?? 1,
            },
            printerSharingType: assetShareOption.PRINT,
        };
    }
    return { printerPayload: {}, printerSharingType: undefined };
};

const handleSmsSharing = (sms: ShareOption) => {
    if (sms.checked && sms.value) {
        return {
            smsPayload: { phoneNumber: sms.value },
            smsSharingType: assetShareOption.SMS,
        };
    }
    return { smsPayload: {}, smsSharingType: undefined };
};
